import { getWallPath } from "building-generation/elements/walls/wall";
import { WallLateralDatumAnchor } from "building-types/datums/datumsTypesEnum";
import type { GeneratedFoundationSubSystem } from "building-types/foundations/foundationTypes";
import type { Level } from "building-types/levels/levelTypes";
import type { Porch } from "building-types/porches/porch";
import type { Room } from "building-types/rooms/roomTypes";
import { RoomClass, RoomEnclosure, RoomKind } from "building-types/rooms/roomTypesEnum";
import { WallType } from "building-types/walls/wallEnums";
import type { WallSystem } from "building-types/walls/wallSytemTypes";
import type { Table } from "common/data/table";
import { roomsOnLevel } from "doc/rooms/roomsOnLevel";
import type { PolyLine2 } from "geom/polyLine2Types";
import { brickLedgeOutlines } from "../elements/foundation/foundationProfiles";
import { solidWallFramingLayer, solidWallSubstrateLayer } from "../elements/walls/wallLayers";

/**
 * This returns the lowest floor height for camera purposes, we could consider also including foundation here if needed.
 */
export function getBottomWallElevation(levels: Table<Level>): number {
  return Object.values(levels).reduce((min, level) => Math.min(min, level.floorElevation), 0);
}

export function getTopWallElevation(levels: Table<Level>): number {
  return Object.values(levels).reduce((max, level) => Math.max(max, level.floorElevation + level.topOfWall), 0);
}

export function makeBuildingDimensionContent({
  levels,
  roomsByLevel,
  rooms,
  porches,
  foundations,
  walls,
  datum,
}: {
  levels: Table<Level>;
  roomsByLevel: Table<string[]>;
  rooms: Table<Room>;
  porches: Table<Porch>;
  foundations: Table<GeneratedFoundationSubSystem>;
  walls: Table<WallSystem>;
  datum: WallLateralDatumAnchor;
}): PolyLine2[] {
  const buildingPolys: PolyLine2[] = [];

  if (Object.values(roomsByLevel).length === 0) {
    return [];
  }

  for (const levelId in levels) {
    for (const { attributes, outline } of roomsOnLevel(levelId, rooms, roomsByLevel)) {
      const { roomClass, roomEnclosure } = attributes;
      if (roomClass === RoomClass.EXTERIOR && roomEnclosure === RoomEnclosure.NONE) {
        continue;
      }
      buildingPolys.push(outline);
    }

    for (const wallId in walls[levelId].walls) {
      const wall = walls[levelId].walls[wallId];
      if (wall.wallType === WallType.VOID) {
        continue;
      }

      const { leftEdge, rightEdge } =
        datum === WallLateralDatumAnchor.OUTSIDE_FACE_OF_SUBSTRATE
          ? solidWallSubstrateLayer(wall)
          : solidWallFramingLayer(wall);

      const wallPath = getWallPath(wall);
      if (wallPath.leftRoomKind === RoomKind.EXTERIOR || wallPath.leftRoomKind === RoomKind.BOUNDARY) {
        buildingPolys.push(leftEdge);
      } else if (wallPath.rightRoomKind === RoomKind.EXTERIOR || wallPath.rightRoomKind === RoomKind.BOUNDARY) {
        buildingPolys.push(rightEdge);
      }
    }
  }

  for (const { floorPerimeters } of Object.values(porches)) {
    buildingPolys.push(...floorPerimeters);
  }

  for (const foundationId in foundations) {
    buildingPolys.push(...brickLedgeOutlines(foundations[foundationId], false));
  }

  return buildingPolys;
}
